/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect, useMemo, useState } from "react";
import queryString from "query-string";
import { useLocation, useHistory } from "react-router-dom";

function getTypedKeys<T>(obj: T): Array<keyof T> {
  return Object.keys(obj as unknown as keyof typeof obj) as Array<
    keyof typeof obj
  >;
}

export type QueryState = {
  page?: string;
  limit?: string;
};

export type HistoryUpdateMode = "push" | "replace";

export type QueryStateKey = keyof QueryState;

export type UpdateQueryStateFn<
  T extends queryString.ParsedQuery<string | number | boolean>
> = (fields: QueryState & T, mode?: HistoryUpdateMode) => void;

export type RemoveQueryStateFn<
  T extends queryString.ParsedQuery<string | number | boolean>
> = (fields: (QueryStateKey | keyof T)[], mode?: HistoryUpdateMode) => void;

function useUpdateUrl() {
  const { pathname, hash } = useLocation();
  const history = useHistory();

  const updateURL = (queryState: QueryState, mode: HistoryUpdateMode) => {
    const search = queryString.stringify(queryState, {
      arrayFormat: "bracket-separator",
      arrayFormatSeparator: ",",
    });

    const newLocation = {
      pathname,
      search,
      hash,
    };

    return mode === "replace"
      ? history.replace(newLocation)
      : history.push(newLocation);
  };

  return { updateURL };
}

export function useQueryState<
  T extends queryString.ParsedQuery<string | number | boolean>
>() {
  const { search } = useLocation();
  const { updateURL } = useUpdateUrl();

  const parseQuery = useCallback(
    (query: string): QueryState & T =>
      queryString.parse(query, {
        arrayFormat: "bracket-separator",
        arrayFormatSeparator: ",",
        parseBooleans: true,
        parseNumbers: true,
        types: {
          searchPhrase: "string",
          providerIds: "string[]",
          cloudAccounts: "string[]",
          createdBy: "string[]",
        },
      }) as QueryState & T,
    []
  );

  const [queryState, setQueryState] = useState<QueryState & T>(() =>
    parseQuery(search)
  );

  useEffect(() => {
    setQueryState(parseQuery(search));
  }, [parseQuery, search]);

  const updateQueryState: UpdateQueryStateFn<T> = (fields, mode = "push") => {
    updateURL({ ...queryState, ...fields }, mode);
  };

  const replaceQueryState = (
    fields: QueryState & T,
    mode: HistoryUpdateMode = "push"
  ) => {
    updateURL({ ...fields }, mode);
  };

  const removeQueryState: RemoveQueryStateFn<T> = (fields, mode = "push") => {
    const fieldsToKeep = getTypedKeys(queryState).filter(
      (key) => !fields.includes(key)
    );
    const savedQueryState = fieldsToKeep.reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: queryState[curr],
      };
    }, {});

    updateURL(savedQueryState, mode);
  };

  const queryStateWithDefaults = useMemo(
    () => ({
      ...queryState,
      limit: parseInt(queryState.limit ?? "20", 10),
      page: parseInt(queryState.page ?? "1", 10),
    }),
    [queryState]
  );

  return {
    queryState: queryStateWithDefaults,
    updateQueryState,
    replaceQueryState,
    removeQueryState,
  };
}
